import { useEffect, useRef, useState } from "react";
import { Grid3x3, Image, Loader2, Minus, Redo2, RotateCcw, Save, Type, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { snapMove, snapWidth, useHistory } from "@/lib/layoutSnap";
import LayoutInvoice from "@/components/invoices/LayoutInvoice";
import LayoutItemPanel from "@/components/invoices/LayoutItemPanel";
import { BLOCKS, DEFAULT_LAYOUT, PAGE_W, SAMPLE_INVOICE, newItem } from "@/lib/invoiceLayout";

function Canvas({ layout, setItems, invoice, settings, selected, setSelected, grid }) {
  const box = useRef(null);
  const page = useRef(null);
  const drag = useRef(null);
  const [scale, setScale] = useState(1);
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(Math.min(1, (e.contentRect.width - 8) / PAGE_W)));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const start = (mode) => (e, k) => {
    e.preventDefault();
    setSelected(k);
    const it = layout.items[k];
    const rects = [...page.current.querySelectorAll("[data-idx]")].map((el) => ({ k: +el.dataset.idx, x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight }));
    const me = rects.find((r) => r.k === k);
    drag.current = { mode, k, sx: e.clientX, sy: e.clientY, x: it.x, y: it.y, w: it.w, h: me?.h || 20, others: rects.filter((r) => r.k !== k && !layout.items[r.k].hidden), key: `${mode}-${k}-${Date.now()}` };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / scale, dy = (e.clientY - d.sy) / scale;
    const free = e.altKey;
    let p;
    if (d.mode === "w") {
      const s = free ? { w: Math.max(10, Math.round(d.w + dx)), guides: [] } : snapWidth({ x: d.x }, d.w + dx, d.others, grid, PAGE_W);
      p = { w: s.w }; setGuides(s.guides);
    } else {
      const s = free ? { x: Math.round(d.x + dx), y: Math.round(d.y + dy), guides: [] } : snapMove({ x: d.x + dx, y: d.y + dy, w: d.w, h: d.h }, d.others, grid, PAGE_W);
      p = { x: s.x, y: s.y }; setGuides(s.guides);
    }
    setItems(layout.items.map((it, i) => (i === d.k ? { ...it, ...p } : it)), d.key);
  };
  const up = () => { drag.current = null; setGuides([]); };
  const gridBg = grid ? { backgroundImage: "radial-gradient(circle, rgba(16,185,129,.35) 1px, transparent 1.2px)", backgroundSize: `${grid}px ${grid}px` } : {};
  return (
    <div ref={box} className="rounded-lg bg-slate-200 p-1 overflow-hidden" onPointerMove={move} onPointerUp={up} onPointerLeave={up} data-testid="layout-canvas">
      <div className="mx-auto" style={{ width: PAGE_W * scale }}>
        <div ref={page} className="relative" style={{ zoom: scale, width: PAGE_W }}>
          <LayoutInvoice invoice={invoice} settings={settings} layout={layout} selected={selected} onPointerDown={start("move")} onResize={start("w")} />
          <div className="absolute inset-0 pointer-events-none" style={gridBg} data-testid="layout-grid" />
          {guides.map((g, i) => (
            <div key={i} className="absolute pointer-events-none bg-fuchsia-500" data-testid="layout-guide"
              style={g.v ? { left: g.pos, top: 0, bottom: 0, width: 1 } : { top: g.pos, left: 0, right: 0, height: 1 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

const isTyping = (el) => el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

export default function InvoiceLayoutEditor() {
  const [settings, setSettings] = useState(null);
  const [invoice, setInvoice] = useState(SAMPLE_INVOICE);
  const hist = useHistory(null);
  const layout = hist.value;
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState("");
  const [grid, setGrid] = useState(8);

  useEffect(() => {
    api.get("/invoice-settings").then((r) => { setSettings(r.data); hist.reset(r.data.layout?.items?.length ? r.data.layout : DEFAULT_LAYOUT); });
    api.get("/invoices").then((r) => { const l = Array.isArray(r.data) ? r.data : r.data.invoices || []; const x = l.find((i) => i.scales?.length) || l[0]; if (x) setInvoice(x); }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || isTyping(document.activeElement)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); hist.undo(); }
      else if (k === "y" || (k === "z" && e.shiftKey)) { e.preventDefault(); hist.redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hist.undo, hist.redo]); // eslint-disable-line react-hooks/exhaustive-deps

  const setItems = (items, key) => hist.set({ ...layout, items }, key);
  const patch = (p) => setItems(layout.items.map((it, i) => (i === selected ? { ...it, ...p } : it)), `patch-${selected}-${Object.keys(p).join()}`);
  const add = (key) => { setItems([...layout.items, newItem(key)]); setSelected(layout.items.length); };
  const save = async () => {
    setBusy("save");
    try { await api.put("/invoice-layout", layout); toast.success("Invoice layout saved — all invoices now use it"); } catch (e) { toast.error(formatError(e)); }
    setBusy("");
  };
  const reset = async () => {
    if (!window.confirm("Go back to the original invoice template? Your custom layout will be removed.")) return;
    setBusy("reset");
    try { await api.delete("/invoice-layout"); hist.set(DEFAULT_LAYOUT); setSelected(null); toast.success("Back to the original template"); } catch (e) { toast.error(formatError(e)); }
    setBusy("");
  };

  if (!settings || !layout) return <div className="p-10"><Loader2 className="animate-spin text-primary" /></div>;
  const it = layout.items[selected];
  return (
    <div className="p-6 lg:p-10 space-y-6 fade-up" data-testid="invoice-layout-editor">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Admin · Invoices</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Invoice layout</h1>
          <p className="text-sm text-muted-foreground mt-1">Drag blocks to move them, pull the green handle to resize. Blocks snap to the grid and line up with each other — hold Alt to move freely.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border" data-testid="layout-history">
            <Button variant="ghost" size="sm" onClick={hist.undo} disabled={!hist.canUndo} title="Undo (Ctrl+Z)" data-testid="layout-undo"><Undo2 size={14} /> Undo</Button>
            <Button variant="ghost" size="sm" onClick={hist.redo} disabled={!hist.canRedo} title="Redo (Ctrl+Y)" data-testid="layout-redo"><Redo2 size={14} /> Redo</Button>
          </div>
          <Select value={String(grid)} onValueChange={(v) => setGrid(Number(v))}>
            <SelectTrigger className="h-9 w-36" data-testid="layout-grid-select"><Grid3x3 size={14} /><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">
              {[["0", "Snap off"], ["4", "Grid 4 px"], ["8", "Grid 8 px"], ["16", "Grid 16 px"], ["24", "Grid 24 px"]].map(([v, l]) => <SelectItem key={v} value={v} data-testid={`grid-opt-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={reset} disabled={!!busy} data-testid="layout-reset">{busy === "reset" ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Reset to original</Button>
          <Button onClick={save} disabled={!!busy} data-testid="layout-save">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save layout</Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <Canvas layout={layout} setItems={setItems} invoice={invoice} settings={settings} selected={selected} setSelected={setSelected} grid={grid} />
        <div className="space-y-4 lg:sticky lg:top-4">
          <div className="border rounded-md p-3 space-y-2">
            <p className="text-sm font-medium">Add</p>
            <div className="grid grid-cols-3 gap-2">
              <Button size="sm" variant="outline" onClick={() => add("text")} data-testid="layout-add-text"><Type size={14} /> Text</Button>
              <Button size="sm" variant="outline" onClick={() => add("logo")} data-testid="layout-add-logo"><Image size={14} /> Logo</Button>
              <Button size="sm" variant="outline" onClick={() => add("line")} data-testid="layout-add-line"><Minus size={14} /> Line</Button>
            </div>
          </div>
          {it ? <LayoutItemPanel it={it} patch={patch} remove={() => { setItems(layout.items.filter((_, i) => i !== selected)); setSelected(null); }} />
            : <p className="text-sm text-muted-foreground border rounded-md p-3">Click a block on the invoice to edit it.</p>}
          <div className="border rounded-md p-2 max-h-72 overflow-y-auto" data-testid="layout-block-list">
            {layout.items.map((x, i) => (
              <button key={x.id || x.key} onClick={() => setSelected(i)} data-testid={`layout-list-${x.id || x.key}`}
                className={`w-full text-left text-sm px-2 py-1.5 rounded flex justify-between transition-colors ${i === selected ? "bg-primary/15 text-primary" : "hover:bg-secondary"}`}>
                <span className={x.hidden ? "line-through text-muted-foreground" : ""}>{BLOCKS[x.key].name}{x.key === "text" ? `: ${x.text.slice(0, 14)}` : ""}</span>
                {x.hidden && <span className="text-xs text-muted-foreground">hidden</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
